import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService, CareerSiteData } from '../services/api';
import { useFavicon } from '../hooks/useFavicon';
import { getTranslations } from '../services/translations';
import { isCustomDomain, validateHostname } from '../utils/routing';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const PrivacyPolicy: React.FC = () => {
  const { clientCode, clientUuid, apCode } = useParams<{ clientCode: string; clientUuid: string; apCode: string }>();
  const isOnCustomDomain = isCustomDomain();
  const clientSlug = clientCode;
  const code = clientCode || clientUuid;
  const navigate = useNavigate();
  const [careerData, setCareerData] = useState<CareerSiteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Seed with the browser language so error/loading states are translated before
  // the company language is known (same pattern as CareerSite/PositionDetails)
  const [translations, setTranslations] = useState<any>(() => getTranslations(navigator.language || 'es-es'));

  // Change favicon dynamically
  useFavicon(careerData?.company.favicon);

  useEffect(() => {
    const fetchCareerData = async () => {
      if (!code && !apCode && !isOnCustomDomain) return;

      try {
        setLoading(true);
        let data: CareerSiteData;
        if (apCode) {
          data = await apiService.getApplySiteData(apCode);
        } else if (isOnCustomDomain && !code) {
          data = await apiService.getCareerSiteByDomain(window.location.hostname);
        } else {
          data = await apiService.getCareerSiteData(code!);
        }

        // Hostname validation: ensure the domain is allowed to serve this client
        const firstApplyUrl = data.jobs?.[0]?.apply_url || data.open_application_jobs?.[0]?.apply_url;
        if (!apCode && !validateHostname(data.company.custom_apply_domain, firstApplyUrl)) {
          navigate('/404', { replace: true });
          return;
        }

        setCareerData(data);

        // Get translations based on company language (default to Spanish)
        const companyTranslations = getTranslations(data.company.language_default || 'es-es');
        setTranslations(companyTranslations);

        // Apply custom CSS from client data (this will override Tailwind's default colors)
        if (data.company.apply_page_css) {
          const style = document.createElement('style');
          style.textContent = data.company.apply_page_css;
          document.head.appendChild(style);
        }
      } catch (err: any) {
        setError(`Error loading privacy policy: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchCareerData();
  }, [code, apCode, isOnCustomDomain]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{translations?.['career_site.loading'] || 'Loading privacy policy...'}</p>
        </div>
      </div>
    );
  }

  if (error || !careerData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{translations?.['career_site.not_found'] || 'Privacy Policy Not Found'}</h1>
          <p className="text-gray-600 mb-8">{error || translations?.['privacy_policy.not_found'] || "The privacy policy you're looking for could not be found."}</p>
          {(clientSlug || apCode || isOnCustomDomain) && (
            <button
              onClick={() => navigate(apCode ? `/sites/${apCode}` : isOnCustomDomain ? '/' : `/${clientSlug}`)}
              className="inline-flex items-center px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition custom-color"
            >
              {translations?.['career_site.back'] || 'Back to Career Site'}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col">
      {/* Logo centered over banner */}
      <div className="relative w-full flex justify-center">
        <img 
          src={careerData.company.logo} 
          alt={`${careerData.company.name} Logo`} 
          className="h-20 w-auto rounded-2xl shadow bg-white z-20 -mb-12 mt-8 border-4 border-white" 
        />
      </div>
      
      {/* Company banner */}
      <div 
        className="banner w-full h-72 bg-cover bg-center flex items-end" 
        style={{ backgroundImage: `url('${careerData.company.main_image}')` }}
      ></div>
      
      {/* Main content */}
      <div className="container max-w-4xl mx-auto bg-white rounded-xl shadow p-10 -mt-16 z-10 relative mb-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {translations?.['privacy_policy.title'] || 'Privacy Policy'}
          </h1>
          <div className="custom-color w-32 h-1 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full mb-4" />
        </div>
        
        {/* Privacy Policy Content */}
        {careerData.company.privacy_policy ? (
          <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => <h1 className="text-lg font-bold mb-3">{children}</h1>,
            h2: ({ children }) => <h2 className="text-base font-bold mb-2">{children}</h2>,
            h3: ({ children }) => <h3 className="text-sm font-bold mb-2">{children}</h3>,
            h4: ({ children }) => <h4 className="text-sm font-semibold mb-2">{children}</h4>,
            p: ({ children }) => <p className="mb-3">{children}</p>,
            ul: ({ children }) => <ul className="list-disc list-inside mb-3">{children}</ul>,
            ol: ({ children }) => <ol className="list-decimal list-inside mb-3">{children}</ol>,
            li: ({ children }) => <li className="mb-1">{children}</li>,
            strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
            em: ({ children }) => <em className="italic">{children}</em>,
            a: ({ href, children }) => (
              <a href={href} className="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            ),
            table: ({ children }) => (
              <div className="overflow-x-auto mb-4">
                <table className="min-w-full border-collapse border border-gray-300 text-sm">{children}</table>
              </div>
            ),
            thead: ({ children }) => <thead className="bg-gray-100">{children}</thead>,
            tbody: ({ children }) => <tbody>{children}</tbody>,
            tr: ({ children }) => <tr className="border-b border-gray-300">{children}</tr>,
            th: ({ children }) => <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{children}</th>,
            td: ({ children }) => <td className="border border-gray-300 px-3 py-2 align-top">{children}</td>,
          }}
        >
          {careerData.company.privacy_policy || ''}
        </ReactMarkdown>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg mb-4">
              {translations?.['privacy_policy.not_available'] || 'Privacy policy is not available at this time.'}
            </p>
            {(clientSlug || apCode || isOnCustomDomain) && (
              <button
                onClick={() => navigate(apCode ? `/sites/${apCode}` : isOnCustomDomain ? '/' : `/${clientSlug}`)}
                className="inline-flex items-center px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition custom-color"
              >
                {translations?.['privacy_policy.back'] || 'Back to Career Site'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="w-full text-center text-xs text-gray-400 py-6 mt-auto">
        <div className="flex flex-col items-center gap-2">
          <div>&copy; {new Date().getFullYear()} Orbio</div>
          {(clientSlug || apCode || isOnCustomDomain) && (
            <a
              href={apCode ? `/sites/${apCode}` : isOnCustomDomain ? '/' : `/${clientSlug}`}
              className="text-gray-500 hover:text-gray-900 transition custom-color"
            >
              {translations?.['privacy_policy.back_to_careers'] || 'Back to Career Site'}
            </a>
          )}
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;

